import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { loadAnalyses } from '../firebase/db';
import { askLLM } from '../ai/askLLM';
import { authedFetch } from '../ai/authedFetch';
import { patentSearchStatus, patentPriorArtContext } from '../ai/patentStatus';
import { AnalyzeDropdownItem } from './AnalyzeDropdownItem';
import { CandidateNotes } from './CandidateNotes';

// ─── Property card labels ───────────────────────────────────────────
const PROP_LABELS = {
  molecular_weight: { label: 'MW', unit: 'g/mol', fmt: v => v.toFixed(1) },
  logP: { label: 'LogP', unit: '', fmt: v => v.toFixed(2) },
  tpsa: { label: 'TPSA', unit: 'Å²', fmt: v => v.toFixed(1) },
  hbd: { label: 'HBD', unit: '', fmt: v => String(v) },
  hba: { label: 'HBA', unit: '', fmt: v => String(v) },
  rotatable_bonds: { label: 'Rot. Bonds', unit: '', fmt: v => String(v) },
  ring_count: { label: 'Rings', unit: '', fmt: v => String(v) },
  aromatic_rings: { label: 'Arom. Rings', unit: '', fmt: v => String(v) },
  heavy_atoms: { label: 'Heavy Atoms', unit: '', fmt: v => String(v) },
  fraction_sp3: { label: 'Fsp³', unit: '', fmt: v => v.toFixed(2) },
  num_stereocenters: { label: 'Stereo', unit: '', fmt: v => String(v) },
};

// ─── IBM FM4M analysis panels ────────────────────────────────────────

function PropertiesPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Computing molecular properties…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const mols = data.molecules || [];
  if (mols.length === 0) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No valid molecules.</div>;

  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}><strong>{mols.length}</strong> molecules analyzed</div>
      <div style={{ maxHeight: 500, overflow: 'auto' }}>
        <table className="table table-dark table-sm mb-0" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>#</th><th>Formula</th><th>MW</th><th>LogP</th><th>TPSA</th>
              <th>HBD</th><th>HBA</th><th>Rings</th><th>Rot.</th><th>Fsp³</th><th>RO5</th>
            </tr>
          </thead>
          <tbody>
            {mols.map((m, i) => (
              <tr key={i} style={{ opacity: m.valid ? 1 : 0.4 }}>
                <td>{i + 1}</td>
                <td style={{ fontWeight: 600 }}>{m.formula || '—'}</td>
                <td>{m.valid ? m.molecular_weight : '—'}</td>
                <td>{m.valid ? m.logP : '—'}</td>
                <td>{m.valid ? m.tpsa : '—'}</td>
                <td>{m.valid ? m.hbd : '—'}</td>
                <td>{m.valid ? m.hba : '—'}</td>
                <td>{m.valid ? m.ring_count : '—'}</td>
                <td>{m.valid ? m.rotatable_bonds : '—'}</td>
                <td>{m.valid ? m.fraction_sp3 : '—'}</td>
                <td>{m.valid ? (
                  <span style={{ color: m.ro5_pass ? '#4ade80' : '#f87171', fontWeight: 600 }}>
                    {m.ro5_pass ? '✓' : `${m.ro5_violations}✗`}
                  </span>
                ) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mols.filter(m => m.valid).length > 1 && (() => {
        const valid = mols.filter(m => m.valid);
        const avg = (key) => (valid.reduce((s, m) => s + (m[key] || 0), 0) / valid.length);
        return (
          <div className="d-flex flex-wrap gap-4 mt-3 pt-2 border-top border-secondary">
            {Object.entries(PROP_LABELS).slice(0, 7).map(([key, { label, unit, fmt }]) => (
              <div key={key}>
                <div style={{ color: '#94a3b8', fontSize: 10 }}>{label} (avg)</div>
                <div style={{ fontWeight: 600 }}>{fmt(avg(key))} {unit}</div>
              </div>
            ))}
            <div>
              <div style={{ color: '#94a3b8', fontSize: 10 }}>RO5 Pass Rate</div>
              <div style={{ fontWeight: 600, color: '#4ade80' }}>{(valid.filter(m => m.ro5_pass).length / valid.length * 100).toFixed(0)}%</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function SimilarityPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Computing similarity matrix…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;

  const labels = data.labels || [];
  const matrix = data.matrix || [];
  const topPairs = data.top_pairs || [];
  const n = labels.length;

  const simColor = (v) => {
    const r = Math.round(15 + v * 40);
    const g = Math.round(25 + v * 200);
    const b = Math.round(35 + v * 213);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}><strong>{n}</strong> molecules compared (Tanimoto similarity)</div>
      {topPairs.length > 0 && (
        <div className="mb-3">
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>Most Similar Pairs</div>
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            <table className="table table-dark table-sm mb-0" style={{ fontSize: 11 }}>
              <thead><tr><th>#i</th><th>#j</th><th>Tanimoto</th></tr></thead>
              <tbody>
                {topPairs.slice(0, 10).map((p, idx) => (
                  <tr key={idx}>
                    <td title={p.smiles_i}>{p.i + 1}</td>
                    <td title={p.smiles_j}>{p.j + 1}</td>
                    <td style={{ fontWeight: 600, color: p.similarity > 0.7 ? '#4ade80' : p.similarity > 0.4 ? '#facc15' : '#94a3b8' }}>
                      {(p.similarity * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {n <= 30 && n > 0 && (
        <div>
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>Similarity Heatmap</div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${n}, 1fr)`, gap: 1, maxWidth: Math.min(n * 24, 600) }}>
            {matrix.map((row, i) =>
              row.map((val, j) => (
                <div key={`${i}-${j}`}
                  title={`${labels[i]} × ${labels[j]}: ${(val * 100).toFixed(1)}%`}
                  style={{
                    width: '100%', aspectRatio: '1', borderRadius: 2,
                    background: simColor(val),
                    fontSize: n <= 15 ? 8 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: val > 0.5 ? '#fff' : '#64748b',
                  }}>
                  {n <= 15 ? (val * 100).toFixed(0) : ''}
                </div>
              ))
            )}
          </div>
          <div className="d-flex justify-content-between mt-1" style={{ fontSize: 10, color: '#64748b', maxWidth: Math.min(n * 24, 600) }}>
            <span>0%</span><span>100%</span>
          </div>
        </div>
      )}
      {n > 30 && (
        <div style={{ color: '#94a3b8', fontSize: 12 }}>Heatmap hidden for {n} molecules (max 30). Use top pairs above.</div>
      )}
    </div>
  );
}

function UMAPPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Running dimensionality reduction…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;

  const points = data.points || [];
  if (points.length === 0) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No points.</div>;

  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const yMin = Math.min(...ys), yMax = Math.max(...ys);
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;
  const W = 560, H = 340, PAD = 24;

  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
        <strong>{data.method?.toUpperCase()}</strong> projection — {points.length} molecules
      </div>
      <svg width={W} height={H} style={{ background: '#1e293b', borderRadius: 6 }}>
        {[0.25, 0.5, 0.75].map(f => (
          <React.Fragment key={f}>
            <line x1={PAD} y1={PAD + f * (H - 2 * PAD)} x2={W - PAD} y2={PAD + f * (H - 2 * PAD)} stroke="#334155" strokeWidth={0.5} />
            <line x1={PAD + f * (W - 2 * PAD)} y1={PAD} x2={PAD + f * (W - 2 * PAD)} y2={H - PAD} stroke="#334155" strokeWidth={0.5} />
          </React.Fragment>
        ))}
        {points.map((p, i) => {
          const cx = PAD + ((p.x - xMin) / xRange) * (W - 2 * PAD);
          const cy = H - PAD - ((p.y - yMin) / yRange) * (H - 2 * PAD);
          return (
            <circle key={i} cx={cx} cy={cy} r={5} fill="#38bdf8" opacity={0.8} stroke="#0f172a" strokeWidth={1}>
              <title>#{p.index + 1}: {p.smiles}</title>
            </circle>
          );
        })}
      </svg>
    </div>
  );
}

function FeatureSummaryPanel({ features, featureNames }) {
  if (!features || features.length === 0) return null;
  const nDims = features[0]?.length || 0;
  if (nDims === 0) return null;

  const means = new Array(nDims).fill(0);
  const stds = new Array(nDims).fill(0);
  const n = features.length;
  for (const row of features) for (let d = 0; d < nDims; d++) means[d] += (row[d] || 0);
  for (let d = 0; d < nDims; d++) means[d] /= n;
  for (const row of features) for (let d = 0; d < nDims; d++) stds[d] += Math.pow((row[d] || 0) - means[d], 2);
  for (let d = 0; d < nDims; d++) stds[d] = Math.sqrt(stds[d] / n);

  const ranked = Array.from({ length: nDims }, (_, i) => ({ idx: i, mean: means[i], std: stds[i] }))
    .sort((a, b) => b.std - a.std).slice(0, 20);
  const maxStd = Math.max(...ranked.map(r => r.std), 1e-9);

  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>
        <strong>{nDims}</strong> feature dimensions × <strong>{n}</strong> molecules
      </div>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 8 }}>Top 20 features by variance</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {ranked.map((r) => {
          const name = featureNames?.[r.idx] || `dim_${r.idx}`;
          const pct = (r.std / maxStd) * 100;
          return (
            <div key={r.idx} className="d-flex align-items-center gap-2" style={{ fontSize: 11 }}>
              <div style={{ width: 90, textAlign: 'right', color: '#94a3b8', flexShrink: 0, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</div>
              <div style={{ flex: 1, height: 14, background: '#1e293b', borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: '#38bdf8', borderRadius: 3, opacity: 0.8 }} />
              </div>
              <div style={{ width: 60, color: '#64748b', fontFamily: 'monospace', fontSize: 10, flexShrink: 0 }}>
                μ={r.mean.toFixed(2)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Analyze ▾ panels (RDKit / analyze-service) ──────────────────────

function MolDescriptorsPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Fetching RDKit descriptors…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;

  const items = data.items || [];
  if (items.length === 0) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No results.</div>;

  const descKeys = [
    ['molecular_weight', 'MW', 'g/mol'], ['logP', 'LogP', ''], ['tpsa', 'TPSA', 'Å²'],
    ['hbd', 'HBD', ''], ['hba', 'HBA', ''], ['qed', 'QED', ''], ['sa_score', 'SA Score', ''],
    ['rotatable_bonds', 'Rot. Bonds', ''], ['aromatic_rings', 'Arom. Rings', ''],
    ['fraction_sp3', 'Fsp³', ''], ['num_stereocenters', 'Stereo', ''], ['ro5_violations', 'RO5 Viol.', ''],
  ];

  const fmtVal = (v) => {
    if (v == null) return '—';
    if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
    return String(v);
  };

  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>{items.length} molecule — RDKit descriptors via analyze service</div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-dark table-sm mb-0" style={{ fontSize: 11, minWidth: 700 }}>
          <thead>
            <tr>
              <th>SMILES</th>
              {descKeys.map(([k, label]) => <th key={k}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td style={{ fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.canonical_smiles || item.input_smiles}>
                  {(item.canonical_smiles || item.input_smiles || '').slice(0, 30)}{(item.canonical_smiles || item.input_smiles || '').length > 30 ? '…' : ''}
                </td>
                {descKeys.map(([k, , unit]) => {
                  const propObj = item.props?.[k];
                  const val = propObj?.value ?? null;
                  const formatted = fmtVal(val);
                  let color = '#e2e8f0';
                  if (k === 'ro5_violations' && val != null) color = val === 0 ? '#4ade80' : val === 1 ? '#facc15' : '#f87171';
                  if (k === 'qed' && val != null) color = val >= 0.6 ? '#4ade80' : val >= 0.4 ? '#facc15' : '#f87171';
                  if (k === 'sa_score' && val != null) color = val <= 3 ? '#4ade80' : val <= 6 ? '#facc15' : '#f87171';
                  return (
                    <td key={k} style={{ color, fontWeight: (k === 'qed' || k === 'ro5_violations') ? 600 : 400 }}>
                      {formatted}{unit && val != null ? <span style={{ color: '#64748b', fontSize: 9 }}> {unit}</span> : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DrugLikenessPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Assessing drug-likeness…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;

  const items = data.items || [];
  if (items.length === 0) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No results.</div>;

  const getVal = (item, key) => item.props?.[key]?.value ?? null;

  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 12 }}>Drug-likeness Assessment — Lipinski RO5 + QED + TPSA</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item, i) => {
          const mw   = getVal(item, 'molecular_weight');
          const logP = getVal(item, 'logP');
          const hbd  = getVal(item, 'hbd');
          const hba  = getVal(item, 'hba');
          const tpsa = getVal(item, 'tpsa');
          const qed  = getVal(item, 'qed');
          const sa   = getVal(item, 'sa_score');
          const ro5v = getVal(item, 'ro5_violations');

          const ro5Rules = [
            { label: 'MW ≤ 500', pass: mw != null && mw <= 500, val: mw != null ? mw.toFixed(1) : null },
            { label: 'LogP ≤ 5', pass: logP != null && logP <= 5, val: logP != null ? logP.toFixed(2) : null },
            { label: 'HBD ≤ 5', pass: hbd != null && hbd <= 5, val: hbd != null ? String(Math.round(hbd)) : null },
            { label: 'HBA ≤ 10', pass: hba != null && hba <= 10, val: hba != null ? String(Math.round(hba)) : null },
          ];
          const tpsaPass = tpsa != null && tpsa < 140;
          const qedLevel = qed == null ? null : qed >= 0.6 ? 'Drug-like' : qed >= 0.4 ? 'Borderline' : 'Not drug-like';
          const qedColor = qed == null ? '#94a3b8' : qed >= 0.6 ? '#4ade80' : qed >= 0.4 ? '#facc15' : '#f87171';
          const overallVerdict = ro5v === 0 ? 'Lipinski Pass' : ro5v === 1 ? 'Minor Violation' : 'Not Drug-like';
          const verdictColor = ro5v === 0 ? '#4ade80' : ro5v === 1 ? '#facc15' : '#f87171';
          const smi = (item.canonical_smiles || item.input_smiles || '').slice(0, 40);

          return (
            <div key={i} style={{ background: '#0a1120', borderRadius: 6, padding: '10px 14px', border: '1px solid #1e293b' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#94a3b8' }}>{smi}{smi.length >= 40 ? '…' : ''}</span>
                <span style={{ fontWeight: 700, color: verdictColor, fontSize: 12 }}>{overallVerdict}</span>
              </div>

              {/* RO5 grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 8 }}>
                {ro5Rules.map((rule) => (
                  <div key={rule.label} style={{ background: '#0f1923', borderRadius: 4, padding: '6px 8px', textAlign: 'center', border: `1px solid ${rule.pass ? '#166534' : '#7f1d1d'}` }}>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{rule.label}</div>
                    <div style={{ fontWeight: 700, color: rule.pass ? '#4ade80' : '#f87171', fontSize: 12 }}>
                      {rule.val != null ? rule.val : '—'} {rule.pass ? '✓' : '✗'}
                    </div>
                  </div>
                ))}
              </div>

              {/* QED + TPSA + SA row */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {qed != null && (
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={{ color: '#94a3b8', fontSize: 10 }}>QED (drug-likeness)</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <div style={{ flex: 1, height: 8, background: '#1e293b', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${qed * 100}%`, height: '100%', background: qedColor, borderRadius: 4 }} />
                      </div>
                      <span style={{ fontWeight: 700, color: qedColor, fontSize: 12 }}>{qed.toFixed(2)}</span>
                    </div>
                    <div style={{ color: qedColor, fontSize: 10, marginTop: 2 }}>{qedLevel}</div>
                  </div>
                )}
                {tpsa != null && (
                  <div>
                    <div style={{ color: '#94a3b8', fontSize: 10 }}>TPSA</div>
                    <div style={{ fontWeight: 700, color: tpsaPass ? '#4ade80' : '#facc15', fontSize: 13 }}>
                      {tpsa.toFixed(1)} Å² {tpsaPass ? '✓' : '(>140 Å²)'}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 10 }}>Oral bioavail. threshold &lt;140 Å²</div>
                  </div>
                )}
                {sa != null && (
                  <div>
                    <div style={{ color: '#94a3b8', fontSize: 10 }}>SA Score</div>
                    <div style={{ fontWeight: 700, color: sa <= 3 ? '#4ade80' : sa <= 6 ? '#facc15' : '#f87171', fontSize: 13 }}>
                      {sa.toFixed(2)} <span style={{ fontSize: 10, fontWeight: 400 }}>{sa <= 3 ? 'Easy synth.' : sa <= 6 ? 'Moderate' : 'Hard synth.'}</span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 10 }}>1=easy → 10=hard</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StructurePanel({ data, loading, smiles: smi }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Generating 2D structure…</div>;

  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      {!data && (
        <div style={{ color: '#94a3b8', fontSize: 12 }}>
          {smi ? 'Fetching structure…' : 'No SMILES available.'}
        </div>
      )}
      {data && !data.ok && (
        <div style={{ color: '#f87171' }}>Error: {data.error || 'Could not render structure'}</div>
      )}
      {data && data.ok && data.svg && (
        <div>
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 8, fontFamily: 'monospace' }}>
            {(data.canonical_smiles || data.smiles || '').slice(0, 80)}
            {(data.canonical_smiles || data.smiles || '').length > 80 ? '…' : ''}
          </div>
          <div style={{
            background: '#fff', borderRadius: 8, display: 'inline-block', padding: 8,
            border: '1px solid #334155', boxShadow: '0 0 0 2px #0f1923',
          }}
            dangerouslySetInnerHTML={{ __html: data.svg }}
          />
          <div style={{ color: '#64748b', fontSize: 10, marginTop: 6 }}>2D structure rendered by RDKit</div>
        </div>
      )}
    </div>
  );
}

// ─── Reports ▾ panels (LLM-generated) ────────────────────────────────

function MolScientificDocPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '16px', color: '#94a3b8', background: '#060d18' }}>Generating scientific document…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '16px', color: '#f87171', background: '#060d18' }}>Error: {data.error || 'Unknown'}</div>;

  const Section = ({ title, content }) => content ? (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontWeight: 700, color: '#94a3b8', fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>{title}</div>
      <div style={{ color: '#cbd5e1', lineHeight: 1.6, fontSize: 13 }}>{content}</div>
    </div>
  ) : null;

  return (
    <div style={{ padding: '16px 20px', background: '#060d18', color: '#e2e8f0', fontSize: 13 }}>
      {data.summary && (
        <div style={{ background: '#0a1120', borderRadius: 6, padding: '10px 14px', marginBottom: 16, borderLeft: '3px solid #7c3aed' }}>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>SUMMARY</div>
          <div style={{ color: '#e2e8f0', lineHeight: 1.6 }}>{data.summary}</div>
        </div>
      )}
      <Section title="Mechanism of Action Hypothesis" content={data.mechanism_hypothesis} />
      <Section title="Synthesis Pathway Discussion" content={data.synthesis_pathway} />
      <Section title="Structural Analogs & Context" content={data.structural_analogs_context} />
      <Section title="Limitations & Open Questions" content={data.limitations} />
    </div>
  );
}

function MolPatentPacketPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '16px', color: '#94a3b8', background: '#060d18' }}>Generating patent packet…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '16px', color: '#f87171', background: '#060d18' }}>Error: {data.error || 'Unknown'}</div>;

  const Section = ({ title, content }) => content ? (
    <div style={{ marginBottom: 20, borderBottom: '1px solid #1e293b', paddingBottom: 16 }}>
      <div style={{ fontWeight: 700, color: '#7c3aed', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{title}</div>
      <div style={{ color: '#cbd5e1', lineHeight: 1.7, fontSize: 13, whiteSpace: 'pre-wrap' }}>{content}</div>
    </div>
  ) : null;

  return (
    <div style={{ padding: '16px 20px', background: '#060d18', color: '#e2e8f0', fontSize: 13 }}>
      {data.patent_abstract && (
        <div style={{ background: '#0a1120', borderRadius: 6, padding: '10px 14px', marginBottom: 16, border: '1px solid #7c3aed' }}>
          <div style={{ color: '#7c3aed', fontWeight: 700, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Abstract</div>
          <div style={{ color: '#e2e8f0', lineHeight: 1.6 }}>{data.patent_abstract}</div>
        </div>
      )}
      <Section title="Composition-of-Matter Claim" content={data.composition_claim} />
      <Section title="Structural Novelty Argument" content={data.novelty_argument} />
      <Section title="Utility Claims" content={data.utility_claims} />
      <Section title="Synthesis Route Claims" content={data.synthesis_route_claims} />
    </div>
  );
}

function MolEconomicReportPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Generating economic report…</div>;
  if (!data) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No data.</div>;
  if (!data.ok && !data.economic_metrics) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const em = data.economic_metrics;
  const volColor = { High: '#f87171', Medium: '#facc15', Low: '#4ade80' };
  const recColor = { High: '#4ade80', Medium: '#facc15', Low: '#f87171' };
  const complexityColor = { low: '#4ade80', medium: '#facc15', high: '#f87171' };
  const riskColor = { low: '#4ade80', medium: '#facc15', high: '#f87171' };
  const sciNum = em ? parseFloat(em.supply_concentration_index) : null;
  const sciColor = sciNum != null ? (sciNum > 0.65 ? '#f87171' : sciNum > 0.40 ? '#facc15' : '#4ade80') : '#94a3b8';

  return (
    <div style={{ fontSize: 13, padding: '12px 16px', background: '#0f1923', color: '#e2e8f0' }}>
      {em && (
        <div className="mb-3" style={{ padding: '12px 16px', background: '#0a1120', border: '1px solid #1e293b', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10 }}>
            Economic Metrics <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(live data)</span>
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <tr><td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20, whiteSpace: 'nowrap', width: '38%' }}>Estimated BOM Cost</td><td style={{ fontSize: 12, fontFamily: 'monospace', paddingBottom: 7, fontWeight: 600, color: '#e2e8f0' }}>{em.cost_low} – {em.cost_high} <span style={{ color: '#94a3b8', fontWeight: 400 }}>/ kg</span></td></tr>
              <tr><td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20 }}>Energy Intensity</td><td style={{ fontSize: 12, paddingBottom: 7 }}><span style={{ color: volColor[em.energy_label] || '#facc15', fontWeight: 600 }}>{em.energy_label}</span><span style={{ color: '#94a3b8', marginLeft: 6 }}>({em.energy_range})</span></td></tr>
              <tr><td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20 }}>Cost Volatility</td><td style={{ fontSize: 12, paddingBottom: 7 }}><span style={{ color: volColor[em.volatility_level] || '#94a3b8', fontWeight: 600 }}>{em.volatility_level}</span>{em.volatility_dominant && <span style={{ color: '#94a3b8', marginLeft: 6 }}>({em.volatility_dominant} dominant contributor)</span>}</td></tr>
              <tr><td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20 }}>Recyclability Index</td><td style={{ fontSize: 12, paddingBottom: 7 }}><span style={{ color: recColor[em.recyclability_level] || '#94a3b8', fontWeight: 600 }}>{em.recyclability_level}</span><span style={{ color: '#94a3b8', marginLeft: 6 }}>(score {em.recyclability_score})</span></td></tr>
              <tr><td style={{ color: '#94a3b8', fontSize: 12, paddingRight: 20 }}>Supply Concentration</td><td style={{ fontSize: 12 }}><span style={{ color: sciColor, fontWeight: 600, fontFamily: 'monospace' }}>{em.supply_concentration_index}</span><span style={{ color: '#94a3b8', marginLeft: 6 }}>— {em.supply_concentration_level} risk</span></td></tr>
            </tbody>
          </table>
          {em.element_details && Object.keys(em.element_details).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>Per-element price breakdown</div>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>{['Element', 'Price / kg', 'Mass frac.', 'Source', 'Date'].map(h => <th key={h} style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, paddingBottom: 4, paddingRight: 12, textAlign: 'left' }}>{h}</th>)}</tr></thead>
                <tbody>{Object.entries(em.element_details).map(([el, d]) => (<tr key={el}><td style={{ fontWeight: 700, fontSize: 11, paddingBottom: 3, paddingRight: 12, color: '#e2e8f0' }}>{el}</td><td style={{ fontFamily: 'monospace', fontSize: 11, paddingBottom: 3, paddingRight: 12 }}>{d.price_label ?? 'N/A'}</td><td style={{ fontFamily: 'monospace', fontSize: 11, paddingBottom: 3, paddingRight: 12, color: '#94a3b8' }}>{d.mass_fraction != null ? (d.mass_fraction * 100).toFixed(1) + '%' : '—'}</td><td style={{ fontSize: 10, paddingBottom: 3, paddingRight: 12, color: '#94a3b8' }}>{d.source ?? '—'}</td><td style={{ fontSize: 10, paddingBottom: 3, color: '#94a3b8' }}>{d.date ?? '—'}</td></tr>))}</tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>
            BOM cost derived from live World Bank commodity prices and USGS MCS 2024 data, weighted by elemental mass fraction.
            {em.data_sources?.length > 0 && <span> Sources: {em.data_sources.join(' · ')}</span>}
          </div>
        </div>
      )}
      {!data.ok && data.economic_metrics && <div className="mb-2" style={{ fontSize: 12, color: '#f87171' }}>LLM qualitative analysis unavailable — quantitative metrics above are still valid.</div>}
      {data.executive_summary && <div className="mb-3" style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, borderLeft: '3px solid #38bdf8' }}><div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Executive Summary</div><div style={{ lineHeight: 1.6 }}>{data.executive_summary}</div></div>}
      <div className="row g-3 mb-3">
        {data.elemental_economics && <div className="col-12 col-md-6"><div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}><div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Elemental Economics</div>{data.elemental_economics.assessment && <div style={{ marginBottom: 8, lineHeight: 1.5 }}>{data.elemental_economics.assessment}</div>}{data.elemental_economics.advantages?.length > 0 && <div className="mb-2">{data.elemental_economics.advantages.map((a, i) => <span key={i} style={{ display: 'inline-block', background: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 4, padding: '2px 8px', fontSize: 11, marginRight: 4, marginBottom: 4 }}>+ {a}</span>)}</div>}{data.elemental_economics.concerns?.length > 0 && <div>{data.elemental_economics.concerns.map((c, i) => <span key={i} style={{ display: 'inline-block', background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 4, padding: '2px 8px', fontSize: 11, marginRight: 4, marginBottom: 4 }}>− {c}</span>)}</div>}</div></div>}
        {data.synthesis_viability && <div className="col-12 col-md-6"><div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}><div className="d-flex align-items-center gap-2 mb-2"><div style={{ color: '#94a3b8', fontSize: 12 }}>Synthesis Viability</div>{data.synthesis_viability.complexity && <span style={{ background: `${complexityColor[data.synthesis_viability.complexity] || '#94a3b8'}22`, color: complexityColor[data.synthesis_viability.complexity] || '#94a3b8', border: `1px solid ${complexityColor[data.synthesis_viability.complexity] || '#94a3b8'}44`, borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{data.synthesis_viability.complexity}</span>}</div>{data.synthesis_viability.assessment && <div style={{ lineHeight: 1.5 }}>{data.synthesis_viability.assessment}</div>}</div></div>}
      </div>
      <div className="row g-3">
        {data.market_potential && <div className="col-12 col-md-6"><div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}><div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Market Potential</div>{data.market_potential.applications?.length > 0 && <div className="mb-2">{data.market_potential.applications.map((app, i) => <span key={i} style={{ display: 'inline-block', background: 'rgba(56,189,248,0.1)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 4, padding: '2px 8px', fontSize: 11, marginRight: 4, marginBottom: 4 }}>{app}</span>)}</div>}{data.market_potential.assessment && <div style={{ lineHeight: 1.5, fontSize: 12 }}>{data.market_potential.assessment}</div>}</div></div>}
        {data.risk_assessment && <div className="col-12 col-md-6"><div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}><div className="d-flex align-items-center gap-2 mb-2"><div style={{ color: '#94a3b8', fontSize: 12 }}>Risk Assessment</div>{data.risk_assessment.overall && <span style={{ background: `${riskColor[data.risk_assessment.overall] || '#94a3b8'}22`, color: riskColor[data.risk_assessment.overall] || '#94a3b8', border: `1px solid ${riskColor[data.risk_assessment.overall] || '#94a3b8'}44`, borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{data.risk_assessment.overall} risk</span>}</div>{data.risk_assessment.factors?.length > 0 && <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, lineHeight: 1.6 }}>{data.risk_assessment.factors.map((f, i) => <li key={i}>{f}</li>)}</ul>}</div></div>}
      </div>
    </div>
  );
}

// ─── Molecular Surrogate panel ──────────────────────────────────────

function MolSurrogatePanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Computing molecular surrogate properties…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const p = data.props || {};
  const ql = data.quality_label || 'unknown';
  const qualityColor = { 'drug-like': '#4ade80', 'lead-like': '#facc15', 'fragment-like': '#38bdf8', 'non-drug-like': '#f87171' };
  const cards = [
    { key: 'homo_lumo_gap',       label: 'HOMO-LUMO Gap' },
    { key: 'dipole_moment_est',   label: 'Dipole Moment' },
    { key: 'polarizability_est',  label: 'Polarizability' },
    { key: 'solvation_energy_est', label: 'Solvation ΔG' },
    { key: 'molar_refractivity',  label: 'Molar Refractivity' },
    { key: 'qed',                 label: 'QED' },
    { key: 'sa_score',            label: 'SA Score' },
    { key: 'molecular_weight',    label: 'MW' },
    { key: 'logP',                label: 'LogP' },
    { key: 'tpsa',                label: 'TPSA' },
    { key: 'fraction_sp3',        label: 'Fsp³' },
    { key: 'heavy_atoms',         label: 'Heavy Atoms' },
  ];
  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div className="d-flex align-items-center gap-2 mb-3">
        {data.formula && <span style={{ fontWeight: 600, fontSize: 14 }}>{data.formula}</span>}
        <span style={{ background: `${qualityColor[ql] || '#94a3b8'}22`, color: qualityColor[ql] || '#94a3b8', border: `1px solid ${qualityColor[ql] || '#94a3b8'}44`, borderRadius: 4, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{ql}</span>
      </div>
      <div className="d-flex flex-wrap gap-3">
        {cards.map(({ key, label }) => {
          const prop = p[key];
          if (!prop || prop.value == null) return null;
          const isHeuristic = (prop.method || '').toLowerCase().includes('heuristic');
          return (
            <div key={key} style={{ background: '#0a1120', border: '1px solid #1e293b', borderRadius: 6, padding: '8px 12px', minWidth: 120 }}>
              <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>{label}{isHeuristic && <span style={{ color: '#f59e0b', marginLeft: 4 }}>~est</span>}</div>
              <div style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 14 }}>
                {typeof prop.value === 'number' ? prop.value.toLocaleString(undefined, { maximumFractionDigits: 3 }) : prop.value}
                {prop.units && <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 11, marginLeft: 4 }}>{prop.units}</span>}
              </div>
              {isHeuristic && <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>{prop.method}</div>}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 8 }}>
        Heuristic estimates (~est) are fast approximations, not DFT-computed. Use for screening and relative ranking.
      </div>
    </div>
  );
}

// ─── Compare panels (amber accent) ──────────────────────────────────

function PubChemLookupPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Querying PubChem…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  if (!data.found) return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ padding: '10px 14px', background: '#0a1120', border: '1px solid #f59e0b', borderRadius: 6 }}>
        <span style={{ color: '#4ade80', fontWeight: 600 }}>Not found in PubChem</span>
        <span style={{ color: '#94a3b8', marginLeft: 8 }}>— this may be a novel compound</span>
      </div>
    </div>
  );
  const p = data.properties || {};
  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>CID {data.cid}</span>
        {data.iupac_name && <span style={{ fontWeight: 600 }}>{data.iupac_name}</span>}
        {data.pubchem_url && <a href={data.pubchem_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#38bdf8' }}>PubChem →</a>}
      </div>
      <div className="d-flex flex-wrap gap-3 mb-2">
        {p.molecular_formula && <div><div style={{ color: '#94a3b8', fontSize: 10 }}>Formula</div><div style={{ fontWeight: 600 }}>{p.molecular_formula}</div></div>}
        {p.molecular_weight != null && <div><div style={{ color: '#94a3b8', fontSize: 10 }}>MW</div><div style={{ fontWeight: 600 }}>{p.molecular_weight} g/mol</div></div>}
        {p.xlogp != null && <div><div style={{ color: '#94a3b8', fontSize: 10 }}>XLogP</div><div style={{ fontWeight: 600 }}>{p.xlogp}</div></div>}
        {p.tpsa != null && <div><div style={{ color: '#94a3b8', fontSize: 10 }}>TPSA</div><div style={{ fontWeight: 600 }}>{p.tpsa} Å²</div></div>}
        {p.complexity != null && <div><div style={{ color: '#94a3b8', fontSize: 10 }}>Complexity</div><div style={{ fontWeight: 600 }}>{p.complexity}</div></div>}
        {p.hbd != null && <div><div style={{ color: '#94a3b8', fontSize: 10 }}>HBD</div><div style={{ fontWeight: 600 }}>{p.hbd}</div></div>}
        {p.hba != null && <div><div style={{ color: '#94a3b8', fontSize: 10 }}>HBA</div><div style={{ fontWeight: 600 }}>{p.hba}</div></div>}
      </div>
      {data.patent_count > 0 && <div style={{ fontSize: 12, color: '#facc15', marginBottom: 4 }}>⚠ {data.patent_count} patent reference{data.patent_count > 1 ? 's' : ''} found</div>}
      {data.synonyms?.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>Synonyms</div>
          <div className="d-flex flex-wrap gap-1">{data.synonyms.map((s, i) => <span key={i} style={{ background: '#1e293b', borderRadius: 4, padding: '1px 6px', fontSize: 11, color: '#cbd5e1' }}>{s}</span>)}</div>
        </div>
      )}
    </div>
  );
}

function ChEMBLActivityPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Querying ChEMBL…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  if (!data.found) return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ padding: '10px 14px', background: '#0a1120', border: '1px solid #f59e0b', borderRadius: 6 }}>
        <span style={{ color: '#4ade80', fontWeight: 600 }}>Not found in ChEMBL</span>
        <span style={{ color: '#94a3b8', marginLeft: 8 }}>— this may be a novel compound</span>
      </div>
    </div>
  );
  const acts = data.activities || [];
  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        {data.chembl_id && <span style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{data.chembl_id}</span>}
        {data.molecule_name && <span style={{ fontWeight: 600 }}>{data.molecule_name}</span>}
        {data.max_phase_label && <span style={{ background: data.max_phase >= 4 ? '#4ade8022' : '#1e293b', color: data.max_phase >= 4 ? '#4ade80' : '#94a3b8', border: `1px solid ${data.max_phase >= 4 ? '#4ade8044' : '#334155'}`, borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{data.max_phase_label}</span>}
        {data.chembl_url && <a href={data.chembl_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#38bdf8' }}>ChEMBL →</a>}
      </div>
      {data.mechanism_of_action && <div style={{ fontSize: 12, color: '#38bdf8', marginBottom: 6 }}>Mechanism: {data.mechanism_of_action}</div>}
      {acts.length > 0 ? (
        <div style={{ maxHeight: 300, overflow: 'auto' }}>
          <table className="table table-dark table-sm mb-0" style={{ fontSize: 11 }}>
            <thead><tr><th>Target</th><th>Type</th><th>Value</th><th>pChEMBL</th></tr></thead>
            <tbody>
              {acts.map((a, i) => (
                <tr key={i}>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.target_name}>{a.target_name || '—'}</td>
                  <td>{a.activity_type || '—'}</td>
                  <td style={{ fontFamily: 'monospace' }}>{a.value != null ? `${a.value} ${a.units || ''}` : '—'}</td>
                  <td style={{ fontWeight: 600, color: a.pchembl_value != null ? (parseFloat(a.pchembl_value) >= 6 ? '#4ade80' : '#facc15') : '#94a3b8' }}>{a.pchembl_value ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: '#94a3b8', fontSize: 12 }}>No bioactivity data recorded.</div>
      )}
    </div>
  );
}

function SimilarDrugsPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Searching for similar compounds…</div>;
  if (!data) return null;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const compounds = data.similar_compounds || [];
  if (compounds.length === 0) return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ padding: '10px 14px', background: '#0a1120', border: '1px solid #f59e0b', borderRadius: 6 }}>
        <span style={{ color: '#4ade80', fontWeight: 600 }}>No similar compounds found</span>
        <span style={{ color: '#94a3b8', marginLeft: 8 }}>— this molecule may be structurally novel</span>
      </div>
    </div>
  );
  return (
    <div style={{ padding: '12px 16px', background: '#0f1923', color: '#e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}><strong>{compounds.length}</strong> similar compounds found (Tanimoto similarity)</div>
      <div style={{ maxHeight: 360, overflow: 'auto' }}>
        <table className="table table-dark table-sm mb-0" style={{ fontSize: 11 }}>
          <thead><tr><th>CID</th><th>Name</th><th>Formula</th><th>MW</th><th>Similarity</th><th></th></tr></thead>
          <tbody>
            {compounds.map((c, i) => (
              <tr key={i}>
                <td style={{ fontFamily: 'monospace' }}>{c.cid}</td>
                <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.name}>{c.name || '—'}</td>
                <td>{c.formula || '—'}</td>
                <td style={{ fontFamily: 'monospace' }}>{c.molecular_weight != null ? c.molecular_weight : '—'}</td>
                <td>
                  {c.similarity != null ? (
                    <span style={{ fontWeight: 600, color: c.similarity >= 0.8 ? '#4ade80' : c.similarity >= 0.5 ? '#facc15' : '#94a3b8' }}>
                      {(c.similarity * 100).toFixed(1)}%
                    </span>
                  ) : '—'}
                </td>
                <td>{c.pubchem_url && <a href={c.pubchem_url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#38bdf8' }}>view</a>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Analysis type definitions ───────────────────────────────────────

const IBM_ANALYSIS_TYPES = [
  { kind: 'properties', label: 'Properties', endpoint: '/molecular/properties', service: 'ibm' },
  { kind: 'similarity', label: 'Similarity', endpoint: '/molecular/similarity', service: 'ibm' },
  { kind: 'umap',       label: 'UMAP',       endpoint: '/molecular/umap',       service: 'ibm' },
];

const MOLECULE_ANALYSIS_TYPES = [
  { kind: 'mol_surrogate',     label: 'Molecular Surrogate',  endpoint: '/analyze/molecule/surrogate', service: 'analyze' },
  { kind: 'mol_descriptors',   label: 'Detailed Descriptors', endpoint: '/analyze/molecule',           service: 'analyze' },
  { kind: 'mol_drug_likeness', label: 'Drug-likeness',        endpoint: '/analyze/molecule',           service: 'analyze' },
  { kind: 'mol_structure',     label: '2D Structure',         endpoint: '/analyze/molecule/structure', service: 'analyze' },
];

const MOLECULE_COMPARE_TYPES = [
  { kind: 'mol_pubchem',       label: 'PubChem Lookup',       endpoint: '/compare/molecule/pubchem_lookup',  service: 'analyze' },
  { kind: 'mol_chembl',        label: 'ChEMBL Activity',      endpoint: '/compare/molecule/chembl_activity', service: 'analyze' },
  { kind: 'mol_similar_drugs', label: 'Similar Known Drugs',  endpoint: '/compare/molecule/similar_drugs',   service: 'analyze' },
];

const MOLECULE_REPORT_TYPES = [
  { kind: 'mol_scientific_doc',   label: 'Scientific Document', custom: true },
  { kind: 'mol_patent_packet',    label: 'Patent Packet',       custom: true },
  { kind: 'mol_economic_report',  label: 'Economic Report',     custom: true },
];

// ─── Main MoleculeTable ──────────────────────────────────────────────

// ─── GT4SD RT measurements (Part D) ──────────────────────────────────────
// Shown only when the caller supplies them. `delta` is logp_rdkit minus the
// effective target, signed and in logP units: negative means the molecule came
// out less lipophilic than asked for. It is the primary output, so it is what
// this column leads with; `within_tolerance` is the service's own convenience
// band and only colours the cell.
const rtColumnHelp = 'RDKit logP of the generated molecule, and its signed distance from the target. Negative means less lipophilic than requested.';

const signed = (value) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;

function renderRtMeasurement(measurement) {
  const logP = measurement && typeof measurement.logp_rdkit === 'number' ? measurement.logp_rdkit : null;
  const delta = measurement && typeof measurement.delta === 'number' ? measurement.delta : null;
  // A molecule the run did not measure is unmeasured. A zero here would read as a
  // perfect hit, which is the one thing it must never say.
  if (logP === null) return <span style={{ color: '#64748b' }}>—</span>;
  const inBand = measurement.within_tolerance === true;
  return (
    <span style={{ color: inBand ? '#4ade80' : '#facc15', fontWeight: 600 }}
      title={measurement.logp_rt != null ? `model self-report ${measurement.logp_rt}` : undefined}>
      {logP.toFixed(2)}
      {delta !== null && (
        <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>({signed(delta)})</span>
      )}
    </span>
  );
}

function MoleculeTable({ smiles, features, featureNames, featureModel, savedMode, initialCache, bulkPersist, measurements, targetValue }) {
  const hasMeasurements = Array.isArray(measurements) && measurements.length > 0;
  const { ibmBaseUrl, analyzeBaseUrl, saveCandidate, unsaveCandidate, isSaved, savedCandidates, toolResultsTs, getCandidateNotes, updateCandidateAnalyses, persistGeneratedCandidates } = useAppContext() || {};
  const ibmBase     = ibmBaseUrl     || 'http://localhost:8011';
  const analyzeBase = analyzeBaseUrl || 'http://localhost:8012';

  const [analysisCache,   setAnalysisCache]   = useState(() => initialCache || {});
  const [analysisLoading, setAnalysisLoading] = useState({});
  const [activeBatchPanels, setActiveBatchPanels] = useState(new Set()); // IBM global: kind
  const [activeAnalyses,  setActiveAnalyses]  = useState(new Set());     // per-mol: 'i::kind'
  const [minimizedPanels, setMinimizedPanels] = useState(new Set());

  const [showFeatures,    setShowFeatures]    = useState(false);
  const [analyzeMenuOpen, setAnalyzeMenuOpen] = useState(null); // mol index or null
  const [compareMenuOpen, setCompareMenuOpen] = useState(null); // mol index or null
  const [reportMenuOpen,  setReportMenuOpen]  = useState(null); // mol index or null
  const [expandedNotes,   setExpandedNotes]   = useState(null); // mol index or null

  const skipPatentRef = useRef(new Set());
  // #38 — Ref mirror so async fetchAnalysis always reads fresh cache
  const analysisCacheRef = useRef(analysisCache);
  useEffect(() => { analysisCacheRef.current = analysisCache; }, [analysisCache]);

  // Flywheel bulk capture: freshly generated molecules (bulkPersist is only passed by
  // the fresh-results call site — never in savedMode). Keys by the service-canonical
  // SMILES when available (H14). Consent gating + per-run dedup live in AppProvider.
  useEffect(() => {
    if (!bulkPersist || savedMode || !smiles?.length || !persistGeneratedCandidates) return;
    const canon = bulkPersist.canonicalSmiles;
    const molFrames = smiles.map((s, i) => ({
      type: 'molecule',
      smiles: (canon && typeof canon[i] === 'string' && canon[i].trim()) ? canon[i] : s,
      index: i,
      rank: i + 1,
    })).filter(f => f.smiles);
    // base: the pooling gate (spec 2026-10-07 Part 0.5); a missing base is never pooled.
    persistGeneratedCandidates(molFrames, { batchKey: bulkPersist.batchKey || '', source: bulkPersist.source || null, base: bulkPersist.base || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkPersist?.batchKey, savedMode]);

  // Load persisted analyses from Firestore on mount, then auto-fetch IBM properties
  // only if no cached data was found. Mirrors ExtxyzTable's Firestore analysis loader.
  const { orgMember, isSuperAdmin } = useAuth() || {};
  const orgId = orgMember?.orgId || (isSuperAdmin ? 'crimsonscan' : null);
  const dbLoadedRef = useRef(false);
  useEffect(() => {
    if (dbLoadedRef.current || !smiles?.length) return;
    dbLoadedRef.current = true;
    // If initialCache was provided (saved page), skip DB load and service fetch
    if (initialCache && Object.keys(initialCache).length > 0) return;
    const doLoad = async () => {
      // Step 1: Load any persisted analyses from Firestore
      let patch = {};
      if (orgId) {
        try {
          const results = await Promise.all(
            smiles.map((smi, idx) =>
              loadAnalyses(orgId, `mol_${smi}`)
                .then(analyses => ({ idx, analyses }))
                .catch(() => ({ idx, analyses: {} }))
            )
          );
          for (const { idx, analyses } of results) {
            for (const [kind, data] of Object.entries(analyses)) {
              if (data) patch[`${idx}::${kind}`] = data;
            }
          }
          // Reconstruct the IBM batch 'properties' result from per-molecule ibm_properties
          const propMols = smiles.map((_, idx) => patch[`${idx}::ibm_properties`] || null);
          if (propMols.some(Boolean)) {
            patch['properties'] = { ok: true, molecules: propMols };
          }
          if (Object.keys(patch).length > 0) {
            setAnalysisCache(prev => ({ ...patch, ...prev }));
          }
        } catch (_) {}
      }
      // Step 2: Auto-fetch IBM properties only if not already cached
      if (!patch['properties']) {
        await fetchBatchAnalysis('properties');
      }
      setActiveBatchPanels(new Set(['properties']));
    };
    doLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, smiles?.length]);

  // ── Helpers ────────────────────────────────────────────────────────

  const setCacheAndLoading = (key, data) => {
    setAnalysisCache(p => ({ ...p, [key]: data }));
    setAnalysisLoading(p => ({ ...p, [key]: false }));
  };

  // Persist a completed per-molecule analysis to Firestore for any molecule (saved or not)
  const persistMolAnalysis = (molIdx, kind, data) => {
    if (!updateCandidateAnalyses || !data) return;
    const smi = smiles?.[molIdx];
    if (!smi) return;
    updateCandidateAnalyses(`mol_${smi}`, { [kind]: data });
  };

  // ── IBM batch analyses (all molecules) ────────────────────────────

  const fetchBatchAnalysis = async (kind) => {
    if (analysisCache[kind]) return;
    setAnalysisLoading(p => ({ ...p, [kind]: true }));
    try {
      const at = IBM_ANALYSIS_TYPES.find(a => a.kind === kind);
      const bodyMap = {
        properties: { smiles },
        similarity: { smiles },
        umap: { smiles, method: 'umap' },
      };
      const resp = await authedFetch(`${ibmBase}${at.endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyMap[kind]),
      });
      const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
      setCacheAndLoading(kind, data);
      // Persist per-molecule property slices so the batch can be reconstructed from Firestore
      if (kind === 'properties' && data?.ok && Array.isArray(data.molecules)) {
        data.molecules.forEach((mol, idx) => {
          if (mol && smiles?.[idx]) persistMolAnalysis(idx, 'ibm_properties', mol);
        });
      }
    } catch (e) {
      setCacheAndLoading(kind, { ok: false, error: e.message });
    }
  };

  const toggleBatchPanel = (kind) => {
    setActiveBatchPanels(prev => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind); else next.add(kind);
      return next;
    });
    fetchBatchAnalysis(kind);
  };

  // ── Per-molecule analyses ─────────────────────────────────────────

  const fetchAnalysis = async (molIdx, kind) => {
    const cacheKey = `${molIdx}::${kind}`;
    const _cache = analysisCacheRef.current; // #38 — read fresh cache via ref
    // Allow re-entry for mol_patent_packet while in intermediate state (searching/clear/warning)
    if (_cache[cacheKey] && !_cache[cacheKey].status) return;
    setAnalysisLoading(p => ({ ...p, [cacheKey]: true }));

    try {
      // mol_descriptors / mol_drug_likeness share one /analyze/molecule call per mol
      if (kind === 'mol_descriptors' || kind === 'mol_drug_likeness') {
        const descKey = `${molIdx}::mol_descriptors`;
        const drugKey = `${molIdx}::mol_drug_likeness`;
        if (!_cache[descKey]) {
          setAnalysisLoading(p => ({ ...p, [descKey]: true, [drugKey]: true }));
          const resp = await authedFetch(`${analyzeBase}/analyze/molecule`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ smiles: [smiles[molIdx]] }),
          });
          const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
          setAnalysisCache(p => ({ ...p, [descKey]: data, [drugKey]: data }));
          setAnalysisLoading(p => ({ ...p, [descKey]: false, [drugKey]: false }));
          if (data?.ok) {
            persistMolAnalysis(molIdx, 'mol_descriptors', data);
            persistMolAnalysis(molIdx, 'mol_drug_likeness', data);
          }
        } else {
          setAnalysisCache(p => ({ ...p, [cacheKey]: p[descKey] }));
          setAnalysisLoading(p => ({ ...p, [cacheKey]: false }));
        }
        return;
      }

      // Molecular Surrogate
      if (kind === 'mol_surrogate') {
        const smi = smiles[molIdx];
        if (!smi) { setCacheAndLoading(cacheKey, { ok: false, error: 'No SMILES' }); return; }
        const resp = await authedFetch(`${analyzeBase}/analyze/molecule/surrogate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ smiles: smi }),
        });
        const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
        setCacheAndLoading(cacheKey, data);
        if (data?.ok) persistMolAnalysis(molIdx, kind, data);
        return;
      }

      // 2D Structure
      if (kind === 'mol_structure') {
        const smi = smiles[molIdx];
        if (!smi) { setCacheAndLoading(cacheKey, { ok: false, error: 'No SMILES' }); return; }
        const resp = await authedFetch(`${analyzeBase}/analyze/molecule/structure`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ smiles: smi }),
        });
        const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
        setCacheAndLoading(cacheKey, data);
        if (data?.ok) persistMolAnalysis(molIdx, kind, data);
        return;
      }

      // ── Compare types (PubChem, ChEMBL, Similar Drugs) ──
      const compareType = MOLECULE_COMPARE_TYPES.find(ct => ct.kind === kind);
      if (compareType) {
        const smi = smiles[molIdx];
        if (!smi) { setCacheAndLoading(cacheKey, { ok: false, error: 'No SMILES' }); return; }
        const resp = await authedFetch(`${analyzeBase}${compareType.endpoint}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ smiles: smi }),
        });
        const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
        setCacheAndLoading(cacheKey, data);
        if (data?.ok) persistMolAnalysis(molIdx, kind, data);
        return;
      }

      // Molecular Scientific Document
      if (kind === 'mol_scientific_doc') {
        const descData = analysisCacheRef.current[`${molIdx}::mol_descriptors`];
        const reprSmiles = smiles[molIdx] || '';
        const reprItem = descData?.ok ? descData.items?.[0] : null;
        const getV = (k) => reprItem?.props?.[k]?.value ?? null;

        const contextLines = [`SMILES: ${reprSmiles}`];
        if (reprItem) {
          const mw = getV('molecular_weight'); if (mw != null) contextLines.push(`MW: ${mw.toFixed(1)} g/mol`);
          const logP = getV('logP'); if (logP != null) contextLines.push(`LogP: ${logP.toFixed(2)}`);
          const tpsa = getV('tpsa'); if (tpsa != null) contextLines.push(`TPSA: ${tpsa.toFixed(1)} Å²`);
          const hbd = getV('hbd'); if (hbd != null) contextLines.push(`H-Bond Donors: ${Math.round(hbd)}`);
          const hba = getV('hba'); if (hba != null) contextLines.push(`H-Bond Acceptors: ${Math.round(hba)}`);
          const qed = getV('qed'); if (qed != null) contextLines.push(`QED: ${qed.toFixed(3)}`);
          const sa = getV('sa_score'); if (sa != null) contextLines.push(`SA Score: ${sa.toFixed(2)} (1=easy, 10=hard)`);
          const ro5 = getV('ro5_violations'); if (ro5 != null) contextLines.push(`RO5 violations: ${Math.round(ro5)}`);
        }

        const systemInstruction = `You are a medicinal chemistry and materials science expert. Given molecular data, write a concise scientific document and return ONLY valid JSON (no markdown) with exactly: {"summary":"string","mechanism_hypothesis":"string","synthesis_pathway":"string","structural_analogs_context":"string","limitations":"string"}. Use precise scientific language. Base all quantitative claims on the provided data.`;
        let llmData = { ok: false };
        try {
          let raw = await askLLM({ systemInstruction, userText: contextLines.join('\n'), response_format: { type: 'json_object' } });
          raw = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
          const match = raw.match(/\{[\s\S]*\}/);
          if (match) raw = match[0];
          llmData = { ok: true, ...JSON.parse(raw) };
        } catch (e) {
          llmData = { ok: false, error: e.message };
        }
        setCacheAndLoading(cacheKey, llmData);
        if (llmData?.ok) persistMolAnalysis(molIdx, kind, llmData);
        return;
      }

      // Molecular Patent Packet
      if (kind === 'mol_patent_packet') {
        // Override initial loading=true — managed manually below
        setAnalysisLoading(p => ({ ...p, [cacheKey]: false }));

        // ── Phase 1: Patent search ──
        // If user has already clicked Generate/Proceed, skip directly to Phase 2
        if (!skipPatentRef.current.has(cacheKey)) {
          // If already waiting for user action (clear/warning), don't re-run the search
          const existingStatus = analysisCacheRef.current[cacheKey]?.status;
          if (existingStatus === 'clear' || existingStatus === 'warning') return;
          setAnalysisCache(p => ({ ...p, [cacheKey]: { status: 'searching' } }));
          let hits = [];
          let risk = 'low';
          let riskDetail = null;
          let sourcesQueried = [];
          let searchStatus = 'ok';
          let sourcesUnavailable = [];
          let searchFailed = false;
          let searchErrorMessage = null;
          try {
            const r = await authedFetch(`${analyzeBase}/patent/search`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ domain: 'molecular', smiles: smiles[molIdx] || '' }),
            });
            if (r.ok) {
              const d = await r.json();
              hits = d.ok ? (d.hits || []) : [];
              risk = d.risk_level || 'low';
              riskDetail = d.risk_detail || null;
              sourcesQueried = d.sources_queried || [];
              searchStatus = d.search_status || 'ok';
              sourcesUnavailable = d.sources_unavailable || [];
            } else {
              // I7b: a non-OK response is a FAILED search, not a clean empty one.
              searchFailed = true;
              searchErrorMessage = `Search service returned HTTP ${r.status}`;
            }
          } catch (e) {
            // I7b: a network error is a FAILED search — previously swallowed here and treated as
            // "clear" (no prior art), a materially wrong claim for a legal document.
            searchFailed = true;
            searchErrorMessage = e?.message || 'Network error';
          }
          // H1: a 200 whose every upstream source failed is a failed search too.
          const status = patentSearchStatus({ searchFailed, hits, searchStatus });
          setAnalysisCache(p => ({ ...p, [cacheKey]: { status, hits, risk, risk_detail: riskDetail, sources_queried: sourcesQueried, sources_unavailable: sourcesUnavailable, search_error_message: searchFailed ? searchErrorMessage : (searchStatus === 'unavailable' ? 'All prior-art sources unavailable' : null) } }));
          return; // wait for user to click Retry / Generate / Proceed Anyway
        }

        // ── Phase 2: LLM generation — user clicked Generate/Proceed Anyway ──
        setAnalysisLoading(p => ({ ...p, [cacheKey]: true }));
        const priorArtHits = analysisCacheRef.current[cacheKey]?.hits || [];
        const cachedMolSearch = analysisCacheRef.current[cacheKey] || {};
        // I7b: when the search failed and the user clicked "Proceed anyway", the disclosure must
        // say the search was UNAVAILABLE — never the false "no prior art found".
        const molPriorArtContext = patentPriorArtContext({
          status: cachedMolSearch.status,
          hits: priorArtHits,
          sourcesQueried: cachedMolSearch.sources_queried,
          sourcesUnavailable: cachedMolSearch.sources_unavailable,
        });

        const descData = analysisCacheRef.current[`${molIdx}::mol_descriptors`];
        const reprSmiles = smiles[molIdx] || '';
        const reprItem = descData?.ok ? descData.items?.[0] : null;
        const getV = (k) => reprItem?.props?.[k]?.value ?? null;
        const contextBlock = [
          `SMILES: ${reprSmiles}`,
          reprItem && getV('molecular_weight') != null ? `MW: ${getV('molecular_weight').toFixed(1)} g/mol` : '',
          reprItem && getV('logP') != null ? `LogP: ${getV('logP').toFixed(2)}` : '',
          reprItem && getV('tpsa') != null ? `TPSA: ${getV('tpsa').toFixed(1)} Å²` : '',
          reprItem && getV('qed') != null ? `QED: ${getV('qed').toFixed(3)}` : '',
          reprItem && getV('sa_score') != null ? `SA Score: ${getV('sa_score').toFixed(2)}` : '',
          reprItem && getV('ro5_violations') != null ? `RO5 violations: ${Math.round(getV('ro5_violations'))}` : '',
          `Prior art search result: ${molPriorArtContext}`,
        ].filter(Boolean).join('\n');

        const mkSys = (section) => `You are a patent attorney specializing in small molecule intellectual property. Given molecular data, write the ${section} section of a composition-of-matter patent. Return ONLY valid JSON (no markdown) matching exactly: {"content":"string"}. Use formal patent language. Be specific and technically precise.`;

        let patentData = { ok: false };
        try {
          const [absRes, compRes, novRes, utilRes, synRes] = await Promise.all([
            askLLM({ systemInstruction: mkSys('abstract (150-250 words)'), userText: contextBlock, response_format: { type: 'json_object' } }),
            askLLM({ systemInstruction: mkSys('composition-of-matter claim'), userText: contextBlock, response_format: { type: 'json_object' } }),
            askLLM({ systemInstruction: mkSys('structural novelty argument'), userText: contextBlock, response_format: { type: 'json_object' } }),
            askLLM({ systemInstruction: mkSys('utility claims (therapeutic or material use)'), userText: contextBlock, response_format: { type: 'json_object' } }),
            askLLM({ systemInstruction: mkSys('synthesis route claims'), userText: contextBlock, response_format: { type: 'json_object' } }),
          ]);

          const parseSection = (raw) => {
            raw = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
            const m = raw.match(/\{[\s\S]*\}/);
            if (m) raw = m[0];
            return JSON.parse(raw).content || '';
          };

          patentData = {
            ok: true,
            patent_abstract:        parseSection(absRes),
            composition_claim:      parseSection(compRes),
            novelty_argument:       parseSection(novRes),
            utility_claims:         parseSection(utilRes),
            synthesis_route_claims: parseSection(synRes),
          };
        } catch (e) {
          patentData = { ok: false, error: e.message };
        }
        setCacheAndLoading(cacheKey, patentData);
        if (patentData?.ok) persistMolAnalysis(molIdx, kind, patentData);
        return;
      }

      // Molecular Economic Report
      if (kind === 'mol_economic_report') {
        const smi = smiles[molIdx];
        if (!smi) { setCacheAndLoading(cacheKey, { ok: false, error: 'No SMILES' }); return; }

        // Step 0: Get molecular formula — reuse cached mol_descriptors if available, else fetch
        let formula = null;
        let reprItem = null;
        const descCached = analysisCacheRef.current[`${molIdx}::mol_descriptors`];
        if (descCached?.ok && descCached.items?.[0]?.formula) {
          formula = descCached.items[0].formula;
          reprItem = descCached.items[0];
        } else {
          try {
            const fResp = await authedFetch(`${analyzeBase}/analyze/molecule`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ smiles: [smi] }),
            });
            if (fResp.ok) {
              const fData = await fResp.json();
              if (fData.ok && fData.items?.[0]?.formula) {
                formula = fData.items[0].formula;
                reprItem = fData.items[0];
                // Cache as mol_descriptors/drug_likeness since we fetched the same endpoint
                setAnalysisCache(p => ({
                  ...p,
                  [`${molIdx}::mol_descriptors`]: fData,
                  [`${molIdx}::mol_drug_likeness`]: fData,
                }));
              }
            }
          } catch (_) {}
        }
        if (!formula) { setCacheAndLoading(cacheKey, { ok: false, error: 'Could not determine molecular formula from SMILES' }); return; }

        // Step 1: Fetch live economic metrics (same endpoint as crystal economic report)
        let economicMetrics = null;
        try {
          const mResp = await authedFetch(`${analyzeBase}/economic/metrics`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ formula }),
          });
          if (mResp.ok) {
            const m = await mResp.json();
            if (m.ok) economicMetrics = {
              cost_low: m.bom_cost_low, cost_high: m.bom_cost_high, cost_raw: m.bom_cost_raw,
              energy_label: m.energy_label, energy_range: m.energy_range,
              volatility_level: m.volatility_level, volatility_dominant: m.volatility_dominant,
              recyclability_level: m.recyclability_level, recyclability_score: m.recyclability_score,
              supply_concentration_index: m.supply_concentration_index,
              supply_concentration_level: m.supply_concentration_level,
              element_details: m.element_details || {}, data_sources: m.data_sources || [],
            };
          }
        } catch (_) {}

        // Step 2: LLM qualitative analysis — molecular framing, same JSON schema as crystal report
        const getV = (k) => reprItem?.props?.[k]?.value ?? null;
        const lines = [`Formula: ${formula}`, `SMILES: ${smi}`];
        const mw = getV('molecular_weight'); if (mw != null) lines.push(`MW: ${mw.toFixed(1)} g/mol`);
        const logP = getV('logP'); if (logP != null) lines.push(`LogP: ${logP.toFixed(2)}`);
        const sa = getV('sa_score'); if (sa != null) lines.push(`SA Score: ${sa.toFixed(2)} (1=easy, 10=hard to synthesize)`);
        const qed = getV('qed'); if (qed != null) lines.push(`QED: ${qed.toFixed(3)}`);
        if (economicMetrics) {
          lines.push('--- Quantitative economic metrics (ground truth — qualitative output must align) ---');
          lines.push(`Estimated BOM cost: ${economicMetrics.cost_low} – ${economicMetrics.cost_high} / kg`);
          lines.push(`Energy intensity: ${economicMetrics.energy_label} (${economicMetrics.energy_range})`);
          lines.push(`Cost volatility: ${economicMetrics.volatility_level}${economicMetrics.volatility_dominant ? ` (${economicMetrics.volatility_dominant} dominant)` : ''}`);
          lines.push(`Recyclability: ${economicMetrics.recyclability_level} (score ${economicMetrics.recyclability_score})`);
          lines.push(`Supply concentration: ${economicMetrics.supply_concentration_index} (${economicMetrics.supply_concentration_level})`);
        }
        const systemInstruction = `You are a chemical manufacturing economist. Given a molecule and its quantitative economic metrics, return ONLY valid JSON with no markdown, matching exactly: {"executive_summary":"string","elemental_economics":{"assessment":"string","concerns":["string"],"advantages":["string"]},"synthesis_viability":{"complexity":"low|medium|high","assessment":"string"},"market_potential":{"applications":["string"],"assessment":"string"},"risk_assessment":{"overall":"low|medium|high","factors":["string"]}}. Your qualitative assessments must be consistent with the quantitative data. Focus on synthetic organic chemistry, reagent availability, and manufacturing scalability.`;
        let llmData = { ok: false };
        try {
          let raw = await askLLM({ systemInstruction, userText: lines.join('\n'), response_format: { type: 'json_object' } });
          raw = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
          const match = raw.match(/\{[\s\S]*\}/);
          if (match) raw = match[0];
          llmData = { ok: true, ...JSON.parse(raw) };
        } catch (e) {
          llmData = { ok: false, error: e.message };
        }
        const econResult = { formula, economic_metrics: economicMetrics, ...llmData };
        setCacheAndLoading(cacheKey, econResult);
        if (llmData?.ok) persistMolAnalysis(molIdx, kind, econResult);
        return;
      }

    } catch (e) {
      setCacheAndLoading(`${molIdx}::${kind}`, { ok: false, error: e.message });
    }
  };

  const toggleAnalysis = (molIdx, kind) => {
    const key = `${molIdx}::${kind}`;
    setActiveAnalyses(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    fetchAnalysis(molIdx, kind);
  };

  // (Auto-fetch of IBM properties is handled inside the Firestore loader useEffect above)

  const truncSmi = (s, max = 50) => s && s.length > max ? s.slice(0, max) + '…' : s;

  // Molecule properties from auto-fetched batch data (for inline table columns)
  const propsMols = analysisCache['properties']?.molecules;
  const getMolProps = (i) => propsMols?.[i] && propsMols[i].valid ? propsMols[i] : null;

  const getPanelLabel = (kind) => {
    const all = [...IBM_ANALYSIS_TYPES, ...MOLECULE_ANALYSIS_TYPES, ...MOLECULE_COMPARE_TYPES, ...MOLECULE_REPORT_TYPES];
    return all.find(a => a.kind === kind)?.label || kind;
  };

  // ── Patent search inline status panel ─────────────────────────────

  const renderPatentStatus = (data, loading, onProceed, onCancel, onRetry) => {
    const violet = '#7c3aed';
    const spinnerRow = (msg, sub) => (
      <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94a3b8' }}>
        <div className="spinner-border spinner-border-sm text-light" role="status" style={{ marginBottom: 8 }} />
        <div style={{ marginTop: 8, fontSize: 14 }}>{msg}</div>
        {sub && <div style={{ marginTop: 4, fontSize: 12, color: '#475569' }}>{sub}</div>}
      </div>
    );
    if (loading) return spinnerRow('Generating patent packet…');
    if (data?.status === 'searching') return spinnerRow('Searching for prior art…', 'Querying patent databases (USPTO, PubChem)');
    if (data?.status === 'search_error') {
      return (
        <div style={{ padding: '24px', color: '#e2e8f0', background: '#0a1628', borderRadius: 8, border: '1px solid #f8717155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 18, color: '#f87171' }}>⚠</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#f87171' }}>Prior-art search failed</span>
          </div>
          <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
            {data.search_error_message ? `Reason: ${data.search_error_message}. ` : ''}
            The prior-art database search could not be completed. You may retry, or proceed anyway —
            the generated packet will explicitly note that prior-art search was unavailable rather
            than claiming none was found.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
            <button className="btn btn-outline-light btn-sm" onClick={onRetry}>Retry</button>
            <button className="btn btn-sm" style={{ background: violet, color: '#fff', border: 'none' }} onClick={onProceed}>
              Proceed anyway
            </button>
          </div>
        </div>
      );
    }
    if (data?.status === 'clear') {
      return (
        <div style={{ padding: '24px', color: '#e2e8f0', background: '#0a1628', borderRadius: 8, border: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 18, color: '#4ade80' }}>✓</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#4ade80' }}>No prior art found</span>
          </div>
          <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
            No matching patents were found{data.sources_queried?.length ? ` in ${data.sources_queried.join(', ')}` : ''}. You may proceed to generate the patent packet.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
            <button className="btn btn-sm" style={{ background: violet, color: '#fff', border: 'none' }} onClick={onProceed}>
              Generate Patent Packet
            </button>
          </div>
        </div>
      );
    }
    if (data?.status === 'warning') {
      const riskColor = data.risk === 'high' ? '#ef4444' : '#f59e0b';
      return (
        <div style={{ padding: '24px', color: '#e2e8f0', background: '#0a1628', borderRadius: 8, border: `1px solid ${riskColor}55` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ background: riskColor, color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>
              {data.risk.toUpperCase()} RISK
            </span>
            <span style={{ color: '#94a3b8', fontSize: 13 }}>Prior art detected — review before proceeding</span>
          </div>
          <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 4, lineHeight: 1.6 }}>
            {data.hits.length} patent{data.hits.length !== 1 ? 's' : ''} found{data.sources_queried?.length ? ` across ${data.sources_queried.join(', ')}` : ''}.
          </p>
          {data.risk_detail && (
            <p style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{data.risk_detail}</p>
          )}
          {data.hits.length > 0 && (
            <div style={{ marginBottom: 16, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1e293b' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Patent ID</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Title</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Source</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.hits.map((h, i) => {
                    const srcColors = { pubchem: '#1e3a5f', uspto_odp: '#3a2d1b' };
                    const srcLabels = { pubchem: 'PubChem', uspto_odp: 'USPTO' };
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #0f172a' }}>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>
                          {(h.google_patents_url || h.url)
                            ? <a href={h.google_patents_url || h.url} target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8' }}>{h.patent_id}</a>
                            : <span style={{ color: '#94a3b8' }}>{h.patent_id}</span>}
                        </td>
                        <td style={{ padding: '5px 8px', color: '#e2e8f0', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {h.title || '—'}
                          {h.assignee && <span style={{ display: 'block', fontSize: 10, color: '#64748b' }}>{h.assignee}</span>}
                        </td>
                        <td style={{ padding: '5px 8px' }}>
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: srcColors[h.source] || '#1e293b', color: '#94a3b8' }}>
                            {srcLabels[h.source] || h.source}
                          </span>
                        </td>
                        <td style={{ padding: '5px 8px', color: '#64748b', fontSize: 11 }}>{h.filing_date || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
            <strong style={{ color: '#94a3b8' }}>Disclaimer:</strong> AI-assisted screening only. Consult a licensed patent professional before filing.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
            <button className="btn btn-warning btn-sm" onClick={onProceed}>Proceed Anyway</button>
          </div>
        </div>
      );
    }
    return null;
  };

  // ── Per-molecule panel renderer ────────────────────────────────────

  const renderMolPanel = (kind, data, loading, molIdx, onPatentProceed, onPatentCancel, onPatentRetry) => {
    switch (kind) {
      case 'mol_surrogate':      return <MolSurrogatePanel data={data} loading={loading} />;
      case 'mol_descriptors':    return <MolDescriptorsPanel data={data} loading={loading} />;
      case 'mol_drug_likeness':  return <DrugLikenessPanel data={data} loading={loading} />;
      case 'mol_structure':      return <StructurePanel data={data} loading={loading} smiles={smiles[molIdx]} />;
      case 'mol_pubchem':        return <PubChemLookupPanel data={data} loading={loading} />;
      case 'mol_chembl':         return <ChEMBLActivityPanel data={data} loading={loading} />;
      case 'mol_similar_drugs':  return <SimilarDrugsPanel data={data} loading={loading} />;
      case 'mol_scientific_doc':   return <MolScientificDocPanel data={data} loading={loading} />;
      case 'mol_economic_report':  return <MolEconomicReportPanel data={data} loading={loading} />;
      case 'mol_patent_packet':
        if (data?.status || loading) return renderPatentStatus(data, loading, onPatentProceed, onPatentCancel, onPatentRetry);
        return <MolPatentPacketPanel data={data} loading={loading} />;
      default: return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div style={{ textAlign: 'left' }}>
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-3">
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{smiles.length} molecules</span>
        {featureModel && <span style={{ color: '#94a3b8', fontSize: 12 }}>Model: <strong style={{ color: '#e2e8f0' }}>{featureModel}</strong></span>}
        {features && features.length > 0 && (
          <span style={{ color: '#94a3b8', fontSize: 12 }}>{features[0]?.length || 0}-dim features</span>
        )}
      </div>


      {/* IBM batch toolbar (Properties / Similarity / UMAP) — hidden in savedMode */}
      {!savedMode && <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
        {IBM_ANALYSIS_TYPES.map(at => {
          const active  = activeBatchPanels.has(at.kind);
          const cached  = !!analysisCache[at.kind];
          const loading = !!analysisLoading[at.kind];
          return (
            <button key={at.kind}
              className={`btn btn-sm ${active ? 'btn-success' : cached ? 'btn-outline-success' : 'btn-outline-info'}`}
              onClick={() => toggleBatchPanel(at.kind)}
              disabled={loading}
            >
              {loading ? '⏳' : cached ? '☑' : '☐'} {at.label}
            </button>
          );
        })}
        {features && features.length > 0 && (
          <button
            className={`btn btn-sm ${showFeatures ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setShowFeatures(p => !p)}
          >Features</button>
        )}
      </div>}

      {/* Click-outside handler */}
      {(analyzeMenuOpen !== null || compareMenuOpen !== null || reportMenuOpen !== null) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          onClick={() => { setAnalyzeMenuOpen(null); setCompareMenuOpen(null); setReportMenuOpen(null); }}
        />
      )}

      {/* Molecule table — per-row Analyze ▾ / Compare ▾ / Reports ▾ */}
      <div style={{ marginBottom: 12 }}>
        <table className="table table-dark table-striped table-hover align-middle mb-0 text-light" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: 45 }}>#</th>
              <th>Molecule</th>
              <th style={{ width: 80 }}>MW</th>
              <th style={{ width: 65 }}>LogP</th>
              {hasMeasurements && (
                <th style={{ width: 110 }} title={rtColumnHelp}>
                  vs target{typeof targetValue === 'number' ? ` (${targetValue})` : ''}
                </th>
              )}
              <th style={{ width: 60 }}>QED</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {smiles.map((s, i) => {
              const mp = getMolProps(i);
              return (
              <React.Fragment key={i}>
                <tr>
                  <td><span style={{ fontWeight: 700, color: i < 3 ? '#38bdf8' : '#94a3b8' }}>{i + 1}</span></td>
                  <td>
                    <div style={{ lineHeight: 1.4 }}>
                      <span style={{ fontWeight: 600 }}>{mp?.formula || '—'}</span>
                      {mp?.ro5_pass != null && (
                        <span style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                          background: mp.ro5_pass ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)',
                          color: mp.ro5_pass ? '#4ade80' : '#f87171',
                          border: `1px solid ${mp.ro5_pass ? '#4ade80' : '#f87171'}` }}>
                          {mp.ro5_pass ? 'RO5 PASS' : `RO5 ${mp.ro5_violations}v`}
                        </span>
                      )}
                      <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace', marginTop: 1 }} title={s}>
                        {truncSmi(s)}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: '#e2e8f0' }}>{mp ? `${mp.molecular_weight?.toFixed?.(1) ?? mp.molecular_weight}` : '—'}</td>
                  <td style={{ color: '#e2e8f0' }}>{mp ? `${mp.logP?.toFixed?.(2) ?? mp.logP}` : '—'}</td>
                  {hasMeasurements && (
                    <td>{renderRtMeasurement(measurements[i])}</td>
                  )}
                  <td>
                    {mp?.qed != null ? (
                      <span
                        title={`QED drug-likeness score: ${mp.qed.toFixed(3)}`}
                        style={{ color: mp.qed >= 0.7 ? '#4ade80' : mp.qed >= 0.4 ? '#facc15' : '#f87171', cursor: 'help', borderBottom: '1px dotted currentColor', fontWeight: 600 }}
                      >{(mp.qed * 100).toFixed(0)}%</span>
                    ) : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    {/* ── left: 2D + analysis + notes ──────────────── */}
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* 2D Structure quick-toggle (mirrors crystal "3D" button) */}
                      <button
                        className={`btn btn-sm ${activeAnalyses.has(`${i}::mol_structure`) ? 'btn-warning' : 'btn-outline-warning'}`}
                        onClick={() => toggleAnalysis(i, 'mol_structure')}
                      >{analysisLoading[`${i}::mol_structure`] ? '2D…' : '2D'}</button>
                      {/* Analyze ▾ */}
                      <div style={{ position: 'relative' }} {...(analyzeMenuOpen === i ? { 'data-dropdown-open': '' } : {})}>
                        <button
                          className={`btn btn-sm ${MOLECULE_ANALYSIS_TYPES.some(at => activeAnalyses.has(`${i}::${at.kind}`)) ? 'btn-success' : 'btn-outline-success'}`}
                          style={{ fontSize: 11 }}
                          onClick={() => { setAnalyzeMenuOpen(analyzeMenuOpen === i ? null : i); setCompareMenuOpen(null); setReportMenuOpen(null); }}
                        >
                          {MOLECULE_ANALYSIS_TYPES.some(at => analysisLoading[`${i}::${at.kind}`]) ? 'analyzing…' : 'Analyze ▾'}
                        </button>
                        {analyzeMenuOpen === i && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, minWidth: 170, background: '#0a1120', border: '1px solid #166534', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 2, padding: '4px 0' }}>
                            {savedMode && (() => {
                              const at = IBM_ANALYSIS_TYPES.find(a => a.kind === 'properties');
                              const active = activeBatchPanels.has('properties');
                              const cached = !!analysisCache['properties'];
                              const ldg = !!analysisLoading['properties'];
                              return (
                                <AnalyzeDropdownItem key="properties"
                                  label={at.label}
                                  active={active}
                                  cached={cached}
                                  loading={ldg}
                                  onClick={() => { toggleBatchPanel('properties'); setAnalyzeMenuOpen(null); }}
                                />
                              );
                            })()}
                            {MOLECULE_ANALYSIS_TYPES.map(at => {
                              const key = `${i}::${at.kind}`;
                              const active  = activeAnalyses.has(key);
                              const cached  = !!analysisCache[key];
                              const ldg     = !!analysisLoading[key];
                              return (
                                <AnalyzeDropdownItem key={at.kind}
                                  label={at.label}
                                  active={active}
                                  cached={cached}
                                  loading={ldg}
                                  onClick={() => { toggleAnalysis(i, at.kind); setAnalyzeMenuOpen(null); }}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {/* Compare ▾ */}
                      <div style={{ position: 'relative' }} {...(compareMenuOpen === i ? { 'data-dropdown-open': '' } : {})}>
                        <button
                          className="btn btn-sm"
                          style={{ fontSize: 11, color: MOLECULE_COMPARE_TYPES.some(ct => activeAnalyses.has(`${i}::${ct.kind}`)) ? '#fff' : '#f59e0b', background: MOLECULE_COMPARE_TYPES.some(ct => activeAnalyses.has(`${i}::${ct.kind}`)) ? '#d97706' : 'transparent', border: '1px solid #f59e0b' }}
                          onClick={() => { setCompareMenuOpen(compareMenuOpen === i ? null : i); setAnalyzeMenuOpen(null); setReportMenuOpen(null); }}
                        >
                          {MOLECULE_COMPARE_TYPES.some(ct => analysisLoading[`${i}::${ct.kind}`]) ? 'comparing…' : 'Compare ▾'}
                        </button>
                        {compareMenuOpen === i && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, minWidth: 200, background: '#0a1120', border: '1px solid #f59e0b', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 2, padding: '4px 0' }}>
                            {MOLECULE_COMPARE_TYPES.map(ct => {
                              const key = `${i}::${ct.kind}`;
                              const active  = activeAnalyses.has(key);
                              const cached  = !!analysisCache[key];
                              const loading = !!analysisLoading[key];
                              return (
                                <button key={ct.kind}
                                  className="btn btn-sm w-100 text-start d-flex align-items-center gap-2"
                                  style={{ color: active ? '#f59e0b' : '#e2e8f0', padding: '6px 12px', border: 'none', background: active ? 'rgba(245,158,11,0.1)' : 'transparent', fontSize: 12 }}
                                  onClick={() => { toggleAnalysis(i, ct.kind); setCompareMenuOpen(null); }}
                                >
                                  <span style={{ width: 16, textAlign: 'center', fontSize: 11 }}>{loading ? '⏳' : cached ? '☑' : '☐'}</span>
                                  <span>{ct.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {/* Reports ▾ */}
                      <div style={{ position: 'relative' }} {...(reportMenuOpen === i ? { 'data-dropdown-open': '' } : {})}>
                        <button
                          className="btn btn-sm"
                          style={{ fontSize: 11, color: MOLECULE_REPORT_TYPES.some(rt => activeAnalyses.has(`${i}::${rt.kind}`)) ? '#fff' : '#a78bfa', background: MOLECULE_REPORT_TYPES.some(rt => activeAnalyses.has(`${i}::${rt.kind}`)) ? '#7c3aed' : 'transparent', border: '1px solid #7c3aed' }}
                          onClick={() => { setReportMenuOpen(reportMenuOpen === i ? null : i); setAnalyzeMenuOpen(null); setCompareMenuOpen(null); }}
                        >
                          {MOLECULE_REPORT_TYPES.some(rt => analysisLoading[`${i}::${rt.kind}`]) ? 'generating…' : 'Reports ▾'}
                        </button>
                        {reportMenuOpen === i && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, minWidth: 180, background: '#0a1120', border: '1px solid #7c3aed', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 2, padding: '4px 0' }}>
                            {MOLECULE_REPORT_TYPES.map(rt => {
                              const key = `${i}::${rt.kind}`;
                              const active  = activeAnalyses.has(key);
                              const cached  = !!analysisCache[key];
                              const loading = !!analysisLoading[key];
                              return (
                                <button key={rt.kind}
                                  className="btn btn-sm w-100 text-start d-flex align-items-center gap-2"
                                  style={{ color: active ? '#a78bfa' : '#e2e8f0', padding: '6px 12px', border: 'none', background: active ? 'rgba(124,58,237,0.15)' : 'transparent', fontSize: 12 }}
                                  onClick={() => { toggleAnalysis(i, rt.kind); setReportMenuOpen(null); }}
                                >
                                  <span style={{ width: 16, textAlign: 'center', fontSize: 11 }}>{loading ? '⏳' : cached ? '☑' : '☐'}</span>
                                  <span>{rt.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <button
                        className={`btn btn-sm ${expandedNotes === i ? 'btn-info' : 'btn-outline-info'}`}
                        style={{ fontSize: 11 }}
                        onClick={() => setExpandedNotes(expandedNotes === i ? null : i)}
                      >
                        Notes{getCandidateNotes?.(`mol_${s}`)?.length ? ' ●' : ''}
                      </button>
                    </div>
                    {/* ── right: save (pushed to far right) ────────── */}
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
                    <button
                      title={isSaved?.({ type: 'molecule', smiles: s }) ? 'Remove from saved' : 'Save for further review'}
                      onClick={() => {
                        const molFrame = { type: 'molecule', smiles: s, index: 0 };
                        const ca = Object.fromEntries(
                          [...MOLECULE_ANALYSIS_TYPES, ...MOLECULE_COMPARE_TYPES, ...MOLECULE_REPORT_TYPES]
                            .map(at => [at.kind, analysisCache[`${i}::${at.kind}`] || null])
                            .filter(([, v]) => v)
                        );
                        const source = { service: 'FM4M', model: featureModel || null, runTs: toolResultsTs || null };
                        isSaved?.(molFrame)
                          ? unsaveCandidate?.(savedCandidates?.find(c => c.fingerprint === `mol_${s}`)?.id)
                          : saveCandidate?.(molFrame, null, null, source, ca);
                      }}
                      style={{ background: 'transparent', border: '1px solid', borderColor: isSaved?.({ type: 'molecule', smiles: s }) ? '#f43f5e' : '#334155', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 14, color: isSaved?.({ type: 'molecule', smiles: s }) ? '#f43f5e' : '#475569', lineHeight: 1 }}
                    >
                      {isSaved?.({ type: 'molecule', smiles: s }) ? '★' : '☆'}
                    </button>
                    </div>{/* end right group */}
                    </div>{/* end outer flex */}
                  </td>
                </tr>
                {/* Inline analysis panels for this molecule */}
                {[...MOLECULE_ANALYSIS_TYPES, ...MOLECULE_COMPARE_TYPES, ...MOLECULE_REPORT_TYPES]
                  .filter(at => activeAnalyses.has(`${i}::${at.kind}`))
                  .map(at => {
                    const key     = `${i}::${at.kind}`;
                    const data    = analysisCache[key];
                    const loading = !!analysisLoading[key];
                    const minimized = minimizedPanels.has(key);
                    const accent  = MOLECULE_REPORT_TYPES.some(r => r.kind === at.kind) ? '#7c3aed' : MOLECULE_COMPARE_TYPES.some(c => c.kind === at.kind) ? '#f59e0b' : '#4ade80';
                    return (
                      <tr key={key}>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div style={{ borderLeft: `3px solid ${accent}`, marginBottom: 1 }}>
                            <div
                              style={{ padding: '4px 12px', fontSize: 11, color: '#94a3b8', background: '#0f1923', borderBottom: minimized ? 'none' : '1px solid #1e293b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                              onClick={() => setMinimizedPanels(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; })}
                            >
                              <span style={{ color: accent, fontWeight: 700, fontSize: 13, width: 14, textAlign: 'center' }}>{minimized ? '+' : '−'}</span>
                              <span style={{ color: accent, fontWeight: 600 }}>{getPanelLabel(at.kind)}</span>
                              <span style={{ marginLeft: 'auto' }}>
                                <button
                                  className="btn btn-sm btn-link p-0"
                                  style={{ color: '#64748b', fontSize: 11 }}
                                  onClick={(e) => { e.stopPropagation(); toggleAnalysis(i, at.kind); }}
                                >✕</button>
                              </span>
                            </div>
                            {!minimized && renderMolPanel(at.kind, data, loading, i,
                              () => {
                                const ck = `${i}::${at.kind}`;
                                skipPatentRef.current.add(ck);
                                fetchAnalysis(i, at.kind);
                              },
                              () => {
                                const ck = `${i}::${at.kind}`;
                                setAnalysisCache(p => { const n = { ...p }; delete n[ck]; return n; });
                                setActiveAnalyses(prev => { const n = new Set(prev); n.delete(ck); return n; });
                              },
                              () => fetchAnalysis(i, at.kind)
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                {expandedNotes === i && (
                  <tr key={`notes-${i}`}>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <CandidateNotes fingerprint={`mol_${s}`} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Feature summary */}
      {showFeatures && features && (
        <div className="mb-3" style={{ border: '1px solid #334155', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '6px 12px', background: '#1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 13 }}>Feature Summary</span>
            <button className="btn btn-sm btn-link text-warning p-0" onClick={() => setShowFeatures(false)}>−</button>
          </div>
          <FeatureSummaryPanel features={features} featureNames={featureNames} />
        </div>
      )}

      {/* IBM batch analysis panels */}
      {IBM_ANALYSIS_TYPES.filter(at => activeBatchPanels.has(at.kind)).map(at => {
        const minimized = minimizedPanels.has(at.kind);
        const data    = analysisCache[at.kind];
        const loading = !!analysisLoading[at.kind];
        return (
          <div key={at.kind} className="mb-3" style={{ border: '1px solid #334155', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '6px 12px', background: '#1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#4ade80', fontWeight: 600, fontSize: 13 }}>{at.label}</span>
              <button
                className="btn btn-sm btn-link text-light p-0"
                onClick={() => setMinimizedPanels(prev => { const n = new Set(prev); if (n.has(at.kind)) n.delete(at.kind); else n.add(at.kind); return n; })}
              >{minimized ? '+' : '−'}</button>
            </div>
            {!minimized && (
              at.kind === 'properties' ? <PropertiesPanel data={data} loading={loading} /> :
              at.kind === 'similarity' ? <SimilarityPanel data={data} loading={loading} /> :
              <UMAPPanel data={data} loading={loading} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default MoleculeTable;
